import { useMemo } from 'react';

import { SideBarDef } from 'ag-grid-community';
import { useQuery } from '@tanstack/react-query';
import { keyBy } from 'lodash';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setQuickFilter } from '@/app/slices/quickFilter';
import { CustomViews } from '@/components/common/custom-views';
import { shipmentStatusOptions, shippingMethodOptions } from '@/constants';
import { ColDef, ValueFormatterParams } from '@/grid/types';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { shippingSitesOptions } from '@/services/shipping';

import { ShipmentStatusTag } from './shipment-status-tag';
import { ShipmentNumberLinkRenderer } from './ShipmentNumberLink';

type ShipmentGridRow = {
  id?: string | null;
  shipmentNumber?: string | null;
  ownerName?: string | null;
  needByDate?: string | null;
  date?: string | null;
  shipFromSiteId?: string | null;
  shipToName?: string | null;
  shipName?: string | null;
  shipmentStatus?: number | null;
  tank?: string | null;
  shippingMethod?: number | null;
  createdByName?: string | null;
  trackingNumber?: string | null;
  comment?: string | null;
  amount?: number | null;
};

export function useShipmentsColumnDefs(): ColDef<ShipmentGridRow>[] {
  const showShipFromSite = useFeatureFlag('Embryo_Ship_From_Site');

  const { data: shippingSites } = useQuery({
    ...shippingSitesOptions(),
    enabled: showShipFromSite,
  });

  const siteMap = useMemo(
    () => keyBy(shippingSites ?? [], (site) => site.id),
    [shippingSites],
  );

  const siteIds = useMemo(
    () => (shippingSites ?? []).map((site) => site.id),
    [shippingSites],
  );

  return useMemo<ColDef<ShipmentGridRow>[]>(
    () => [
      {
        headerName: '',
        checkboxSelection: true,
        width: 50,
        suppressMovable: true,
        lockPosition: true,
      },
      {
        field: 'shipmentNumber',
        headerName: 'Shipment Number',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
        cellRenderer: ShipmentNumberLinkRenderer,
      },
      {
        field: 'ownerName',
        headerName: 'Account',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
      },
      {
        field: 'needByDate',
        headerName: 'Need By Date',
        width: 120,
        filter: 'agDateColumnFilter',
        wrapHeaderText: true,
        valueFormatter: dateOnlyFormatter,
      },
      {
        field: 'date',
        headerName: 'Ship Date',
        width: 120,
        filter: 'agDateColumnFilter',
        wrapHeaderText: true,
        valueFormatter: dateOnlyFormatter,
      },
      ...(showShipFromSite
        ? [
            {
              field: 'shipFromSiteId' as const,
              headerName: 'Ship From Site',
              width: 150,
              filter: 'agSetColumnFilter',
              wrapHeaderText: true,
              filterParams: {
                values: siteIds,
                valueFormatter: (
                  params: ValueFormatterParams<ShipmentGridRow, string | null>,
                ) => siteMap[String(params.value)]?.name ?? '',
              },
              valueFormatter: (
                params: ValueFormatterParams<ShipmentGridRow, string | null>,
              ) => siteMap[String(params.value)]?.name ?? '',
            },
          ]
        : []),
      {
        field: 'shipToName',
        headerName: 'Ship To',
        width: 150,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
      },
      {
        field: 'shipName',
        headerName: 'Ship Name',
        width: 150,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
      },
      {
        field: 'shipmentStatus',
        headerName: 'Shipment Status',
        width: 120,
        wrapHeaderText: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: shipmentStatusOptions.map((option) => option.value),
          valueFormatter: (
            params: ValueFormatterParams<ShipmentGridRow, number>,
          ) =>
            shipmentStatusOptions.find(
              (option) => option.value === params.value,
            )?.label,
        },
        valueFormatter: (
          params: ValueFormatterParams<ShipmentGridRow, number>,
        ) =>
          shipmentStatusOptions.find((option) => option.value === params.value)
            ?.label ?? '',
        cellRenderer: ShipmentStatusTag,
      },
      {
        field: 'tank',
        headerName: 'Tank/Incubator',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
      },
      {
        field: 'shippingMethod',
        headerName: 'Shipping Method',
        width: 120,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: shippingMethodOptions.map((option) => option.value),
          valueFormatter: (
            params: ValueFormatterParams<ShipmentGridRow, number>,
          ) =>
            shippingMethodOptions.find(
              (option) => option.value === params.value,
            )?.label,
        },
        wrapHeaderText: true,
        valueFormatter: (
          params: ValueFormatterParams<ShipmentGridRow, number>,
        ) =>
          shippingMethodOptions.find((option) => option.value === params.value)
            ?.label ?? '',
      },
      {
        field: 'createdByName',
        headerName: 'Created By',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
      },
      {
        field: 'trackingNumber',
        headerName: 'Tracking Number',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
      },
      {
        field: 'comment',
        headerName: 'Comment',
        width: 120,
        filter: 'agTextColumnFilter',
        wrapHeaderText: true,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 120,
        filter: 'agNumberColumnFilter',
        wrapHeaderText: true,
      },
    ],
    [showShipFromSite, siteIds, siteMap],
  );
}

export const shipmentsGridSideBar: SideBarDef = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
    },
    {
      id: 'customViews',
      labelDefault: 'Custom Views',
      labelKey: 'customViews',
      iconKey: 'custom-views',
      toolPanel: CustomViews,
      toolPanelParams: {
        setQuickFilter,
        useAppDispatch,
        useAppSelector,
      },
    },
  ],
};
